import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { cleanEntity } from 'app/shared/util/entity-utils';
import { EntityState, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { ICycle, defaultValue } from 'app/shared/model/cycle.model';

export interface ICycleClasse {
  id: string;
  nom: string;
  cycleId?: string | null;
}

export interface ICycleMatiere {
  id: string;
  nom: string;
  color?: string;
  avecSousMatieres?: boolean;
  cycleMatiereId?: string;
  coefficientSource?: CoefficientSource;
}

export interface ICycleSousMatiere {
  id: string;
  nom: string;
  cycleMatiereId: string;
  ordre?: number;
  actif?: boolean;
}

export interface ICycleCoefficient {
  id?: string;
  matiereId: string;
  classeId: string;
  coefficient: number | string;
}

export type CycleScoringMode = 'COEFFICIENT' | 'PONDERATION';
export type CoefficientSource = 'MATIERE' | 'SOUS_MATIERES';

export interface ICycleScoringConfiguration {
  matieres: { cycleMatiereId: string; coefficientSource: CoefficientSource }[];
  coefficients: { matiereId: string; classeId: string; coefficient: number }[];
  sousMatiereCoefficients: { sousMatiereId: string; classeId: string; coefficient: number }[];
}

export interface ICycleMatieresSaveConfiguration {
  matieres: { matiereId: string; avecSousMatieres: boolean }[];
}

export interface ICycleScoringMode {
  cycleId: string;
  scoringMode: CycleScoringMode;
  usesCoefficients: boolean;
  noteScale: number;
}

const initialState: EntityState<ICycle> = {
  loading: false,
  errorMessage: null,
  entities: [] as ReadonlyArray<ICycle>,
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
  totalItems: 0,
};

const apiUrl = 'api/cycles';

export const getCycleScoringConfiguration = createAsyncThunk(
  'cycle/fetch_scoring_configuration',
  async (cycleId: string) => (await axios.get<ICycleScoringConfiguration>(`${apiUrl}/${cycleId}/scoring-configuration`)).data,
  { serializeError: serializeAxiosError }
);

export const saveCycleScoringConfiguration = createAsyncThunk(
  'cycle/save_scoring_configuration',
  async ({ cycleId, configuration }: { cycleId: string; configuration: ICycleScoringConfiguration }) =>
    (await axios.put<ICycleScoringConfiguration>(`${apiUrl}/${cycleId}/scoring-configuration`, configuration)).data,
  { serializeError: serializeAxiosError }
);

const asArray = <T>(value: T[] | { content?: T[] } | null | undefined): T[] => {
  if (Array.isArray(value)) {
    return value;
  }
  const content = (value as { content?: T[] } | undefined | null)?.content;
  if (Array.isArray(content)) {
    return content;
  }
  return [];
};

export const getCycles = createAsyncThunk('cycle/fetch_cycle_list', async (ecoleId: string | number) => {
  const requestUrl = `${apiUrl}?ecoleId=${ecoleId}`;
  return axios.get<ICycle[]>(requestUrl);
});

export const getCycle = createAsyncThunk(
  'cycle/fetch_cycle',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ICycle>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getCycleClasses = createAsyncThunk(
  'cycle/fetch_cycle_classes',
  async (cycleId: string | number) => {
    const response = await axios.get<ICycleClasse[]>(`${apiUrl}/${cycleId}/classes`);
    return asArray(response.data);
  },
  { serializeError: serializeAxiosError }
);

export const getCycleScoringMode = createAsyncThunk(
  'cycle/fetch_cycle_scoring_mode',
  async (cycleId: string | number) => {
    const response = await axios.get<ICycleScoringMode>(`${apiUrl}/${cycleId}/scoring-mode`);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const getEcoleClasses = createAsyncThunk(
  'cycle/fetch_ecole_classes',
  async (ecoleId: string | number) => {
    const response = await axios.get<ICycleClasse[]>(`api/classes?ecoleId=${ecoleId}`);
    return asArray(response.data);
  },
  { serializeError: serializeAxiosError }
);

export const linkCycleClasses = createAsyncThunk(
  'cycle/link_cycle_classes',
  async ({ cycleId, classeIds }: { cycleId: string; classeIds: string[] }) => {
    await Promise.all(
      classeIds.map(classeId =>
        axios.patch(`api/classes/${classeId}`, { id: classeId, cycleId }, { headers: { 'Content-Type': 'application/merge-patch+json' } })
      )
    );
  },
  { serializeError: serializeAxiosError }
);

export const unlinkCycleClasse = createAsyncThunk(
  'cycle/unlink_cycle_classe',
  async (classeId: string) => {
    await axios.patch(
      `api/classes/${classeId}`,
      { id: classeId, cycleId: null },
      { headers: { 'Content-Type': 'application/merge-patch+json' } }
    );
  },
  { serializeError: serializeAxiosError }
);

export const getCycleMatieres = createAsyncThunk(
  'cycle/fetch_cycle_matieres',
  async (cycleId: string | number) => {
    const response = await axios.get<ICycleMatiere[]>(`${apiUrl}/${cycleId}/matieres`);
    return asArray(response.data);
  },
  { serializeError: serializeAxiosError }
);

export const getEcoleMatieres = createAsyncThunk(
  'cycle/fetch_ecole_matieres',
  async (ecoleId: string | number) => {
    const response = await axios.get<ICycleMatiere[]>(`api/ecoles/${ecoleId}/matiere-ecoles?size=500`);
    return asArray(response.data);
  },
  { serializeError: serializeAxiosError }
);

export const linkExistingCycleMatiere = createAsyncThunk(
  'cycle/link_existing_cycle_matiere',
  async ({ cycleId, matiereId, avecSousMatieres }: { cycleId: string; matiereId: string; avecSousMatieres: boolean }) => {
    const response = await axios.post<ICycleMatiere>(`${apiUrl}/${cycleId}/matieres`, {
      id: matiereId,
      avecSousMatieres,
    });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const createCycleMatiere = createAsyncThunk(
  'cycle/create_cycle_matiere',
  async ({ cycleId, nom, ecoleId, avecSousMatieres }: { cycleId: string; nom: string; ecoleId?: string; avecSousMatieres: boolean }) => {
    const response = await axios.post<ICycleMatiere>(`${apiUrl}/${cycleId}/matieres`, {
      nom,
      ecoleId,
      avecSousMatieres,
    });
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const saveCycleMatieres = createAsyncThunk(
  'cycle/save_cycle_matieres',
  async ({ cycleId, configuration }: { cycleId: string; configuration: ICycleMatieresSaveConfiguration }) => {
    const response = await axios.put<ICycleMatiere[]>(`${apiUrl}/${cycleId}/matieres`, configuration);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const unlinkCycleMatiere = createAsyncThunk(
  'cycle/unlink_cycle_matiere',
  async ({ cycleId, matiereId }: { cycleId: string; matiereId: string }) => {
    await axios.delete(`${apiUrl}/${cycleId}/matieres/${matiereId}`);
  },
  { serializeError: serializeAxiosError }
);

export const getCycleSousMatieres = createAsyncThunk(
  'cycle/fetch_cycle_sous_matieres',
  async ({ cycleMatiereId, cycleId, matiereId }: { cycleMatiereId?: string; cycleId?: string; matiereId?: string }) => {
    const params = new URLSearchParams();
    if (cycleMatiereId) params.set('cycleMatiereId', cycleMatiereId);
    if (cycleId) params.set('cycleId', cycleId);
    if (matiereId) params.set('matiereEcoleId', matiereId);
    const response = await axios.get<ICycleSousMatiere[]>(`api/sous-matieres?${params.toString()}`);
    return asArray(response.data);
  },
  { serializeError: serializeAxiosError }
);

export const createCycleSousMatiere = createAsyncThunk(
  'cycle/create_cycle_sous_matiere',
  async (entity: { nom: string; cycleMatiereId: string }) => {
    const response = await axios.post<ICycleSousMatiere>('api/sous-matieres', entity);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const updateCycleSousMatiere = createAsyncThunk(
  'cycle/update_cycle_sous_matiere',
  async (entity: ICycleSousMatiere) => {
    const response = await axios.put<ICycleSousMatiere>(`api/sous-matieres/${entity.id}`, entity);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const deleteCycleSousMatiere = createAsyncThunk(
  'cycle/delete_cycle_sous_matiere',
  async (id: string) => {
    await axios.delete(`api/sous-matieres/${id}`);
  },
  { serializeError: serializeAxiosError }
);

export const getMatiereCoefficients = createAsyncThunk(
  'cycle/fetch_matiere_coefficients',
  async (matiereId: string) => {
    const response = await axios.get<ICycleCoefficient[]>(`api/coefficients/matiere/${matiereId}`);
    return asArray(response.data);
  },
  { serializeError: serializeAxiosError }
);

export const saveCycleCoefficients = createAsyncThunk(
  'cycle/save_cycle_coefficients',
  async ({
    matiereId,
    classes,
    coefficientValues,
    coefficientsByClasseId,
  }: {
    matiereId: string;
    classes: ICycleClasse[];
    coefficientValues: Record<string, number | null>;
    coefficientsByClasseId: Record<string, ICycleCoefficient>;
  }) => {
    await Promise.all(
      classes.map(classe => {
        const value = coefficientValues[classe.id];
        const existingCoefficient = coefficientsByClasseId[classe.id];

        if (!value && existingCoefficient?.id) {
          return axios.delete(`api/coefficients/${existingCoefficient.id}`).then(() => undefined);
        }

        if (!value) {
          return Promise.resolve(undefined);
        }

        const entity = {
          ...existingCoefficient,
          matiereId,
          classeId: classe.id,
          coefficient: value,
        };

        return (
          existingCoefficient?.id ? axios.put(`api/coefficients/${existingCoefficient.id}`, entity) : axios.post('api/coefficients', entity)
        ).then(() => undefined);
      })
    );
  },
  { serializeError: serializeAxiosError }
);

export const createCycle = createAsyncThunk(
  'cycle/create_cycle',
  async (entity: ICycle, thunkAPI) => {
    const result = await axios.post<ICycle>(`${apiUrl}`, cleanEntity(entity));
    thunkAPI.dispatch(getCycles(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateCycle = createAsyncThunk(
  'cycle/update_cycle',
  async (entity: ICycle, thunkAPI) => {
    const result = await axios.put<ICycle>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getCycles(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteCycle = createAsyncThunk(
  'cycle/delete_cycle',
  async (entity: { id: string; ecoleId: string }, thunkAPI) => {
    const requestUrl = `${apiUrl}/${entity.id}`;
    const result = await axios.delete<ICycle>(requestUrl);
    thunkAPI.dispatch(getCycles(entity.ecoleId));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export type CycleState = Readonly<typeof initialState>;

export const CycleSlice = createSlice({
  name: 'cycle',
  initialState: initialState as CycleState,
  reducers: {
    reset() {
      return initialState;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getCycle.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })
      .addCase(deleteCycle.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(createCycle, updateCycle), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getCycles, getCycle), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isFulfilled(getCycles), (state, action) => {
        state.loading = false;
        state.entities = action.payload.data;
        state.totalItems = action.payload.data?.length || 0;
      })
      .addMatcher(isPending(createCycle, updateCycle, deleteCycle), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isRejected(createCycle, updateCycle, deleteCycle), (state, action) => {
        state.errorMessage = action.error.message || null;
        state.updateSuccess = false;
        state.updating = false;
      });
  },
});

export const { reset } = CycleSlice.actions;

export default CycleSlice.reducer;
